import React from "react";
import { Link } from "react-router-dom";
import { Plus, Check } from "lucide-react";
import { useCart } from "../context/CartContext";
import { STOCK_LABELS } from "../mock";
import { useToast } from "../hooks/use-toast";
import VialRender from "./VialRender";

const toneClasses = {
  green: "bg-emerald-50 text-emerald-700 ring-emerald-200",
  amber: "bg-amber-50 text-amber-700 ring-amber-200",
  red: "bg-red-50 text-red-600 ring-red-200",
};

const ProductCard = ({ product }) => {
  const { addItem } = useCart();
  const { toast } = useToast();
  const stock = STOCK_LABELS[product.stock] || STOCK_LABELS["in-stock"];
  const soldOut = product.stock === "out-of-stock";
  const isPreorder = product.stock === "pre-order";

  const handleAdd = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (soldOut) return;
    addItem(product, 1);
    toast({ title: "Added to cart", description: `${product.name} · $${product.price.toFixed(2)}` });
  };

  return (
    <Link
      to={`/product/${product.id}`}
      className="product-card group flex flex-col rounded-2xl border border-slate-200 bg-white overflow-hidden hover:shadow-xl hover:shadow-blue-900/5 hover:border-blue-200"
    >
      <div className="relative aspect-square overflow-hidden">
        <VialRender product={product} className="transition-transform duration-500 group-hover:scale-105" />
        <div className="absolute top-3 left-3 flex flex-col gap-1.5">
          <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-semibold ring-1 ${toneClasses[stock.tone]}`}>
            {product.stock === "low" ? `${product.stockCount} Left` : stock.label}
          </span>
        </div>
        {product.compareAt && (
          <span className="absolute top-3 right-3 rounded-full bg-blue-600 text-white px-2.5 py-1 text-[11px] font-bold">
            Save ${(product.compareAt - product.price).toFixed(0)}
          </span>
        )}
      </div>

      <div className="flex flex-col flex-1 p-4">
        <span className="text-[11px] font-semibold uppercase tracking-wider text-blue-600">
          {product.purity} HPLC · {product.size}
        </span>
        <h3 className="mt-1 font-display font-bold text-slate-900 leading-snug line-clamp-2 group-hover:text-blue-700 transition-colors">
          {product.name}
        </h3>
        <p className="mt-1.5 text-xs text-slate-500 line-clamp-2 flex-1">{product.short}</p>

        <div className="mt-4 flex items-end justify-between">
          <div>
            {product.compareAt && (
              <span className="block text-xs text-slate-400 line-through">${product.compareAt.toFixed(2)}</span>
            )}
            <span className="text-lg font-extrabold text-slate-900">${product.price.toFixed(2)}</span>
          </div>
          <button
            onClick={handleAdd}
            disabled={soldOut}
            className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-semibold transition-all ${
              soldOut
                ? "bg-slate-100 text-slate-400 cursor-not-allowed"
                : "bg-blue-600 text-white hover:bg-blue-700 active:scale-95"
            }`}
          >
            {soldOut ? "Sold Out" : isPreorder ? "Pre-Order" : <><Plus className="h-4 w-4" /> Add</>}
          </button>
        </div>
      </div>
    </Link>
  );
};

export default ProductCard;
